'use client';

import '@chewy/kib-carousels-styles/dist/styles/kib-carousel.css';
import '@chewy/kib-interstitials-styles/dist/styles/kib-spinner.css';

import { KibCarousel, KibCarouselItem } from '@chewy/kib-carousels-react';
import { KibPlayIcon } from '@chewy/kib-icons-react';
import { KibSpinner } from '@chewy/kib-interstitials-react';
import { lazy, Suspense, useState } from 'react';

import { ANALYTICS_TAG, useAnalyticsContext } from '../Analytics/AnalyticsProvider';

import styles from './styles.module.scss';

const ReactPlayer = lazy(() => import('react-player'));

import bowenthegolden from '@/assets/videos/bowenthegolden.jpg';
// @ts-expect-error - MP4 imports are configured in next.config but TS doesn't recognize them
import bowenthegoldenVideo from '@/assets/videos/bowenthegolden.mp4';
import chastitynolan from '@/assets/videos/chastitynolan.jpg';
// @ts-expect-error - MP4 imports are configured in next.config but TS doesn't recognize them
import chastitynolanVideo from '@/assets/videos/chastitynolan.mp4';
import imbluethesiberian from '@/assets/videos/imbluethesiberian.jpg';
// @ts-expect-error - MP4 imports are configured in next.config but TS doesn't recognize them
import imbluethesiberianVideo from '@/assets/videos/imbluethesiberian.mp4';
import mandiekai from '@/assets/videos/mandiekai.jpg';
// @ts-expect-error - MP4 imports are configured in next.config but TS doesn't recognize them
import mandiekaiVideo from '@/assets/videos/mandiekai.mp4';
import reignstormpit from '@/assets/videos/reignstormpit.jpg';
// @ts-expect-error - MP4 imports are configured in next.config but TS doesn't recognize them
import reignstormpitVideo from '@/assets/videos/reignstormpit.mp4';
import thegoldenbros from '@/assets/videos/thegoldenbros.jpg';
// @ts-expect-error - MP4 imports are configured in next.config but TS doesn't recognize them
import thegoldenbrosVideo from '@/assets/videos/thegoldenbros.mp4';
import thelavelleshow from '@/assets/videos/thelavelleshow.jpg';
// @ts-expect-error - MP4 imports are configured in next.config but TS doesn't recognize them
import thelavelleshowVideo from '@/assets/videos/thelavelleshow.mp4';
import trufflecosta from '@/assets/videos/trufflecosta.jpg';
// @ts-expect-error - MP4 imports are configured in next.config but TS doesn't recognize them
import trufflecostaVideo from '@/assets/videos/trufflecosta.mp4';
import wallymeetsworld from '@/assets/videos/wallymeetsworld.jpg';
// @ts-expect-error - MP4 imports are configured in next.config but TS doesn't recognize them
import wallymeetsworldVideo from '@/assets/videos/wallymeetsworld.mp4';

export const InfluencerVideos = ({}: {}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState<number | null>(null);
  const { track } = useAnalyticsContext();

  const videos = [
    {
      id: 1,
      videoUrl: bowenthegoldenVideo,
      image: bowenthegolden.src,
      influencer: '@bowen_thegolden',
      socialLink: 'https://www.instagram.com/bowen_thegolden',
      platform: 'Instagram',
    },
    {
      id: 2,
      videoUrl: chastitynolanVideo,
      image: chastitynolan.src,
      influencer: '@chastitynolan',
      socialLink: 'https://www.tiktok.com/@chastitynolan',
      platform: 'TikTok',
    },
    {
      id: 3,
      videoUrl: imbluethesiberianVideo,
      image: imbluethesiberian.src,
      influencer: '@imbluethesiberian',
      socialLink: 'https://www.instagram.com/imbluethesiberian/',
      platform: 'Instagram',
    },
    {
      id: 4,
      videoUrl: mandiekaiVideo,
      image: mandiekai.src,
      influencer: '@mandiekaii',
      socialLink: 'https://www.instagram.com/mandiekaii/',
      platform: 'Instagram',
    },
    {
      id: 5,
      videoUrl: reignstormpitVideo,
      image: reignstormpit.src,
      influencer: '@reignstormpit',
      socialLink: 'https://www.tiktok.com/@reignstormpit',
      platform: 'TikTok',
    },
    {
      id: 6,
      videoUrl: thegoldenbrosVideo,
      image: thegoldenbros.src,
      influencer: '@thegoldenbros_t.b',
      socialLink: 'https://www.instagram.com/thegoldenbros_t.b/',
      platform: 'Instagram',
    },
    {
      id: 7,
      videoUrl: thelavelleshowVideo,
      image: thelavelleshow.src,
      influencer: '@thelavelleshow',
      socialLink: 'https://www.tiktok.com/@thelavelleshow',
      platform: 'TikTok',
    },
    {
      id: 8,
      videoUrl: trufflecostaVideo,
      image: trufflecosta.src,
      influencer: '@trufflecosta',
      socialLink: 'https://www.instagram.com/trufflecosta/',
      platform: 'Instagram',
    },
    {
      id: 9,
      videoUrl: wallymeetsworldVideo,
      image: wallymeetsworld.src,
      influencer: '@wally.meets.world',
      socialLink: 'https://www.tiktok.com/@wally.meets.world',
      platform: 'TikTok',
    },
  ];

  const handleVideoContainerClick = (videoId: number) => {
    setSelectedVideo(videoId);

    track(ANALYTICS_TAG.NAME_VIDEO_CLICKED, {
      eventCategory: ANALYTICS_TAG.CATEGORY_VIDEOS,
      eventAction: ANALYTICS_TAG.EVENT_CLICK,
      eventLabel: 'get-real-video-play',
    });
  };

  const handleOnPlaying = () => {
    setIsLoading(false);
  };

  const handleOnEnded = () => {
    setSelectedVideo(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent, videoId: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleVideoContainerClick(videoId);
    }
  };

  return (
    <div className={styles.root}>
      <KibCarousel className={styles.videosCarousel}>
        {videos.map((video) => (
          <KibCarouselItem key={video.id} className={styles.videosCarouselItem}>
            <div
              className={styles.videoContainer}
              onClick={() => handleVideoContainerClick(video.id)}
              onKeyDown={(e) => handleKeyDown(e, video.id)}
              role="button"
              tabIndex={0}
              aria-label={`Play video from ${video.influencer}`}
            >
              <img
                src={video.image}
                alt={`An update from ${video.influencer}`}
                className={styles.image}
              />

              {selectedVideo !== video.id && (
                <div className={styles.playIconOverlay}>
                  <KibPlayIcon className={styles.playIcon} />
                </div>
              )}

              {selectedVideo && selectedVideo === video.id && (
                <div
                  className={styles.playerContainer}
                  tabIndex={-1}
                  aria-label={`Video player for ${video.influencer}`}
                >
                  {isLoading && <KibSpinner className={styles.spinningLoader} />}

                  <Suspense fallback={<KibSpinner className={styles.spinningLoader} />}>
                    <ReactPlayer
                      onPlaying={handleOnPlaying}
                      src={video.videoUrl}
                      playing
                      controls
                      width="100%"
                      height="100%"
                      onEnded={handleOnEnded}
                    />
                  </Suspense>
                </div>
              )}
            </div>

            <div className={styles.influencerInfo}>
              <a
                href={video.socialLink}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.influencerLink}
              >
                {video.influencer}
                <span className={styles.visuallyHidden}>&nbsp;on {video.platform}</span>
              </a>
            </div>
          </KibCarouselItem>
        ))}
      </KibCarousel>
    </div>
  );
};
